import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Copy, Pin, Users, BarChart3, UserPlus } from "lucide-react";

/**
 * One-time modal that fires the first time is_leader flips true.
 * Persists dismissal via PATCH /api/settings { leader_onboarding_seen: true }.
 */
export default function LeaderOnboarding() {
  const { user, setUser } = useAuth();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (user && user.is_leader && user.leader_onboarding_seen === false) {
      setOpen(true);
    }
  }, [user?.is_leader, user?.leader_onboarding_seen]);

  const dismiss = async (opened) => {
    setOpen(false);
    try {
      await api.patch("/settings", { leader_onboarding_seen: true });
      setUser({ ...user, leader_onboarding_seen: true });
    } catch (e) {
      console.error("dismiss leader onboarding failed", e);
    }
  };

  if (!open) return null;
  const items = [
    { icon: Copy, title: "Your invite code", body: "Each group has a 6-character code on its page. Share it with your people; they'll paste it into Groups → Join." },
    { icon: Pin, title: "Pin an announcement", body: "One pinned message per group — perfect for meeting time, ground rules, or the week's focus. Late joiners see it first." },
    { icon: BarChart3, title: "See member progress", body: "Open your group to see how each person is walking through the eleven weeks. Not to grade — to know where to encourage." },
    { icon: UserPlus, title: "Share the load", body: "Promote a trusted member to co-leader so preparing the week isn't always on you." },
  ];

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) dismiss(); }}>
      <DialogContent data-testid="leader-onboarding" className="max-w-lg rounded-none border">
        <DialogHeader className="text-left">
          <div className="text-xs uppercase tracking-[0.2em] text-primary">You're leading now</div>
          <DialogTitle className="font-serif text-3xl leading-tight mt-2">A few things worth knowing.</DialogTitle>
          <DialogDescription className="text-muted-foreground mt-2">
            Leading a group is less about being the expert and more about walking with people. Here's what you have at hand.
          </DialogDescription>
        </DialogHeader>
        <ul className="mt-4 space-y-5">
          {items.map((it, i) => (
            <li key={it.title} className="flex gap-4">
              <it.icon className="w-4 h-4 text-primary mt-1 shrink-0" />
              <div>
                <div className="font-medium">{it.title}</div>
                <div className="text-sm text-muted-foreground mt-1 leading-relaxed">{it.body}</div>
              </div>
            </li>
          ))}
        </ul>
        <DialogFooter className="mt-6">
          <Button onClick={dismiss} data-testid="leader-onboarding-dismiss" className="rounded-full w-full sm:w-auto">
            Got it — let's begin
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
